import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../lib/api.js';

/**
 * Une couleur par journée, pour que les étapes d'un même jour se reconnaissent
 * sur la carte sans lire les libellés. Au-delà de huit jours, on recommence :
 * deux journées éloignées partageant une teinte ne prêtent pas à confusion.
 */
const COULEURS = ['#2e6d9c', '#8a5cc4', '#1f8f6b', '#c26a2e', '#b03f6a', '#2f7fa8', '#6b7f2e', '#8a4a8a'];

const couleurDuJour = (dayIndex) => COULEURS[(dayIndex - 1) % COULEURS.length];

/**
 * Carte des étapes situées.
 *
 * Leaflet est piloté directement, sans surcouche React : la carte est un objet
 * impératif à durée de vie longue, et l'envelopper dans un composant
 * n'apporterait ici qu'une dépendance de plus.
 */
export function Carte({ itineraire, tripId, onChange }) {
  const conteneur = useRef(null);
  const carte = useRef(null);
  const couche = useRef(null);
  const [localisation, setLocalisation] = useState(null);
  const [erreur, setErreur] = useState(null);

  const jours = itineraire.days ?? [];
  const situees = jours.flatMap((jour) =>
    jour.items
      .filter((item) => typeof item.lat === 'number' && typeof item.lng === 'number')
      .map((item) => ({ ...item, dayIndex: jour.dayIndex })),
  );
  const sansPosition = jours.reduce(
    (total, jour) => total + jour.items.filter((i) => typeof i.lat !== 'number').length,
    0,
  );

  // Création de la carte, une seule fois.
  useEffect(() => {
    if (carte.current || !conteneur.current) return undefined;

    carte.current = L.map(conteneur.current, { scrollWheelZoom: false });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      // Attribution obligatoire : c'est la condition d'usage des tuiles.
      attribution: '&copy; contributeurs <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(carte.current);
    couche.current = L.layerGroup().addTo(carte.current);

    // Leaflet mémorise la taille de son conteneur au moment du montage. Ici il
    // naît dans un onglet dont la largeur n'est pas encore établie : il ne
    // couvrait qu'une bande et cadrait sur un quartier. On le tient informé
    // plutôt que d'invalider une fois et d'espérer.
    const observateur = new ResizeObserver(() => carte.current?.invalidateSize());
    observateur.observe(conteneur.current);

    return () => {
      observateur.disconnect();
      carte.current?.remove();
      carte.current = null;
    };
  }, []);

  // Redessin des marqueurs à chaque changement d'itinéraire.
  useEffect(() => {
    if (!carte.current || !couche.current) return;

    couche.current.clearLayers();
    if (situees.length === 0) return;

    for (const jour of jours) {
      const points = jour.items.filter((i) => typeof i.lat === 'number');
      if (points.length === 0) continue;

      const couleur = couleurDuJour(jour.dayIndex);

      // Le trait relie les étapes dans l'ordre de la journée : il donne à voir
      // les allers-retours qu'un simple semis de points masquerait.
      if (points.length > 1) {
        L.polyline(points.map((p) => [p.lat, p.lng]), {
          color: couleur,
          weight: 3,
          opacity: 0.5,
          dashArray: '6 6',
        }).addTo(couche.current);
      }

      points.forEach((item, rang) => {
        const marqueur = L.marker([item.lat, item.lng], {
          icon: L.divIcon({
            className: 'marqueur',
            html: `<span class="marqueur__pastille" style="background:${couleur}">${rang + 1}</span>`,
            iconSize: [26, 26],
            iconAnchor: [13, 13],
          }),
        });
        marqueur.bindPopup(
          `<strong>Jour ${jour.dayIndex}${item.time ? ` · ${item.time}` : ''}</strong><br>${item.title}`,
        );
        marqueur.addTo(couche.current);
      });
    }

    // Le cadrage attend que la mise en page soit faite : calculé dans le même
    // tour que le rendu, il porte sur des dimensions qui ne sont pas encore
    // celles de l'écran.
    const bornes = L.latLngBounds(situees.map((p) => [p.lat, p.lng]));
    const cadrer = () => {
      if (!carte.current) return;
      carte.current.invalidateSize();
      carte.current.fitBounds(bornes, { padding: [40, 40], maxZoom: 14 });
    };
    const image = requestAnimationFrame(cadrer);

    return () => cancelAnimationFrame(image);
  }, [itineraire]);

  async function localiser() {
    setErreur(null);
    setLocalisation('en cours');
    try {
      const reponse = await api.post(`/trips/${tripId}/itinerary/geocode`);
      onChange(reponse.trip);
      setLocalisation(
        reponse.localisees > 0
          ? `${reponse.localisees} étape${reponse.localisees > 1 ? 's' : ''} localisée${reponse.localisees > 1 ? 's' : ''}`
          : 'aucune étape n’a pu être localisée',
      );
    } catch (echec) {
      setErreur(echec.message);
      setLocalisation(null);
    }
  }

  return (
    <div className="carte-bloc">
      {situees.length === 0 ? (
        <p className="aide aide--haut">
          Aucune étape n’a de position pour l’instant.
        </p>
      ) : (
        <div ref={conteneur} className="carte-leaflet" />
      )}

      {situees.length > 0 && (
        <ul className="legende">
          {jours
            .filter((jour) => jour.items.some((i) => typeof i.lat === 'number'))
            .map((jour) => (
              <li key={jour.dayIndex} className="legende__entree">
                <span
                  className="legende__pastille"
                  style={{ background: couleurDuJour(jour.dayIndex) }}
                  aria-hidden="true"
                />
                Jour {jour.dayIndex}
              </li>
            ))}
        </ul>
      )}

      {sansPosition > 0 && (
        <div className="actions">
          <button
            className="bouton bouton--discret bouton--auto"
            type="button"
            disabled={localisation === 'en cours'}
            onClick={localiser}
          >
            {localisation === 'en cours'
              ? 'Recherche en cours…'
              : `Localiser les ${sansPosition} étape${sansPosition > 1 ? 's' : ''} restante${sansPosition > 1 ? 's' : ''}`}
          </button>
          <p className="aide">
            La recherche interroge OpenStreetMap, à raison d’une étape par
            seconde : c’est la condition d’usage d’un service gratuit. Comptez
            une seconde par étape.
          </p>
        </div>
      )}

      {localisation && localisation !== 'en cours' && (
        <p className="aide" role="status">
          {localisation}
        </p>
      )}

      {erreur && (
        <p className="erreur" role="alert">
          {erreur}
        </p>
      )}
    </div>
  );
}
